/**
 * SHEIN 尺寸属性（attribute_type=2）尺码表工具。
 * 文档：仅主销售 → 不关联次销售；有次销售 → relate_sale_* 关联 SKU 维销售属性。
 */

import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
  simplifyPlatformLabel,
} from '@/shein-platform/utils/sheinAttributeTemplate';

export const SIZE_CHART_BASE_KEY = '_base';

export function isCustomSpecKey(key) {
  return String(key || '').startsWith('custom:');
}

export function makeCustomSpecKey(label) {
  return `custom:${encodeURIComponent(String(label || '').trim())}`;
}

export function customLabelFromKey(key) {
  try {
    return decodeURIComponent(String(key).slice('custom:'.length));
  } catch {
    return String(key).slice('custom:'.length);
  }
}

/** SKU 行在尺码表/选择器中的稳定 key */
export function skuSizeKey(sku) {
  if (!sku) return '';
  if (sku.sizeIsCustom || sku.sizeExtraValue) {
    return makeCustomSpecKey(sku.sizeExtraValue || sku.sizeLabel || '');
  }
  return String(sku.sizeValueId || '');
}

/** 从 SKU 行推导尺码表行 key（与供应表一致） */
export function deriveSizeKeysFromSkcList(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

/**
 * 尺码表纵轴行：优先用 SKU 上的规格（与供应表一致），避免 selectedSizeIds 残留模板重复行
 *（例如两个 mid-platform → 平台报「周长单选不允许多个值」）。
 * @returns {{ id: string, name: string, custom: boolean }[]}
 */
export function resolveSizeChartRows(selectedSizeIds = [], sizeAttr = null, skcList = []) {
  const labelByKey = {};
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      const name = String(sku.sizeLabel || sku.sizeExtraValue || '').trim();
      if (key && name) labelByKey[key] = name;
    });
  });

  const fromSkc = deriveSizeKeysFromSkcList(skcList);
  const ids = fromSkc.length
    ? fromSkc
    : [...new Set((selectedSizeIds || []).map(String).filter(Boolean))];

  // 按「关联规格签名」去重：custom:mid-platform 与明文 mid-platform 只保留一行
  const seenRelate = new Set();
  const rows = [];
  ids.forEach((id) => {
    const relateKey = isCustomSpecKey(id)
      ? `v:${customLabelFromKey(id)}`
      : (/^\d+$/.test(id) ? `id:${id}` : `v:${id}`);
    if (seenRelate.has(relateKey)) return;
    seenRelate.add(relateKey);

    if (isCustomSpecKey(id)) {
      rows.push({
        id,
        name: simplifyPlatformLabel(customLabelFromKey(id)) || customLabelFromKey(id),
        custom: true,
      });
      return;
    }
    const opt = (sizeAttr?.values || []).find((v) => String(v.id) === id);
    rows.push({
      id,
      name: attributeValueDisplayLabel(opt)
        || simplifyPlatformLabel(labelByKey[id])
        || labelByKey[id]
        || id,
      custom: false,
    });
  });
  return rows;
}

/**
 * 是否需要按次销售属性展开尺码表（文档 3.6）。
 * 有次销售属性定义且已有规格值（SKU 或 selectedSizeIds）→ 矩阵。
 */
export function sizeChartNeedsSaleAxis(sizeAttr, selectedSizeIds = [], skcList = []) {
  if (!sizeAttr?.attributeId) return false;
  if ((selectedSizeIds || []).length > 0) return true;
  return deriveSizeKeysFromSkcList(skcList).length > 0;
}

function resolveSaleAttributeId(form = {}) {
  const fromForm = Number(form.sizeSaleAttributeId);
  if (Number.isFinite(fromForm) && fromForm > 0) return fromForm;
  for (const skc of form.skcList || []) {
    for (const sku of skc?.skuRows || []) {
      const id = Number(sku?.sizeAttributeId);
      if (Number.isFinite(id) && id > 0) return id;
    }
  }
  return 0;
}

/** 行 key → 关联次销售属性值入参 */
function relateSaleFieldsForRowKey(key, saleAttrId) {
  if (!saleAttrId) return null;
  const out = { relate_sale_attribute_id: saleAttrId };
  if (isCustomSpecKey(key)) {
    const label = String(customLabelFromKey(key) || '').trim();
    if (!label) return null;
    out.relate_sale_attribute_value = label;
    return out;
  }
  if (/^\d+$/.test(key)) {
    const valueId = Number(key);
    if (Number.isFinite(valueId) && valueId > 0) {
      out.relate_sale_attribute_value_id = valueId;
      return out;
    }
  }
  // 兼容误存的明文规格名（非 custom: 前缀）
  const plain = String(key || '').trim();
  if (!plain || plain === SIZE_CHART_BASE_KEY) return null;
  out.relate_sale_attribute_value = plain;
  return out;
}

/**
 * 尺码表横轴属性是否可按「次销售规格 × 尺寸属性」展开。
 * 文档：attribute_mode=0/4 用手输 extra；mode=3 下拉单选「某个 attribute_id 只能有一组数据」。
 * 类目里「周长」常被标成 mode=3：若仍按每个尺码各传一条，平台会报「单选不允许录入多个值」。
 */
export function isSizeChartMatrixAttr(attr) {
  const mode = Number(attr?.mode);
  if (!Number.isFinite(mode)) return true;
  return mode === 0 || mode === 4;
}

/** mode=3：文档写「某个 attribute_id 只能有一组数据」（商品属性常见） */
export function isSizeChartSingleSelectAttr(attr) {
  return Number(attr?.mode) === 3;
}

/**
 * 多尺码 + mode=3 尺寸属性：平台规则互相冲突——
 * 每尺码都传 →「单选不允许录入多个值」；只传一条 → 其它尺码「数值不能为空」。
 * 因此多尺码时不传/不展示该类属性（可选则跳过；若必填则校验拦截）。
 */
export function isSizeChartApiUnsupportedMultiSizeAttr(attr, saleRowCount = 0) {
  return isSizeChartSingleSelectAttr(attr) && Number(saleRowCount) > 1;
}

export function partitionSizeMeasureAttrs(sizeAttrs = [], saleRowCount = 0) {
  const matrix = [];
  const single = [];
  const unsupported = [];
  (sizeAttrs || []).forEach((attr) => {
    if (!attr?.attributeId) return;
    if (isSizeChartApiUnsupportedMultiSizeAttr(attr, saleRowCount)) {
      unsupported.push(attr);
      return;
    }
    if (isSizeChartSingleSelectAttr(attr)) single.push(attr);
    else matrix.push(attr);
  });
  return { matrixAttrs: matrix, singleAttrs: single, unsupportedAttrs: unsupported };
}

function readChartMeasure(chart, rowKey, attrId) {
  const measures = chart?.[rowKey];
  if (!measures || typeof measures !== 'object') return '';
  const raw = measures[attrId] ?? measures[String(attrId)];
  return raw === '' || raw == null ? '' : String(raw).trim();
}

/** mode=3：从基础行或各规格行取第一个非空值 */
function pickSingleSelectMeasureValue(chart, attrId, rowKeys = []) {
  const fromBase = readChartMeasure(chart, SIZE_CHART_BASE_KEY, attrId);
  if (fromBase) return fromBase;
  for (const key of rowKeys) {
    const v = readChartMeasure(chart, key, attrId);
    if (v) return v;
  }
  // 兼容模板残留：任意行上的该属性
  for (const [key, measures] of Object.entries(chart || {})) {
    if (key === SIZE_CHART_BASE_KEY) continue;
    if (!measures || typeof measures !== 'object') continue;
    const v = readChartMeasure(chart, key, attrId);
    if (v) return v;
  }
  return '';
}

function pushSizeMeasureItem(list, attr, raw, relate = null) {
  const attrId = Number(attr.attributeId);
  const values = Array.isArray(attr.values) ? attr.values : [];
  const asValueId = Number(raw);
  const hit = values.find((v) => String(v.id) === raw || Number(v.id) === asValueId);
  if (hit?.id != null) {
    list.push({
      attribute_id: attrId,
      attribute_value_id: Number(hit.id),
      ...(relate || {}),
    });
    return;
  }
  list.push({
    attribute_id: attrId,
    attribute_extra_value: raw,
    ...(relate || {}),
  });
}

/**
 * 组装 size_attribute_list。
 * - mode=0/4：有次销售时按规格矩阵展开，必须带 relate_sale_*
 * - mode=3 且多尺码：不传（平台规则冲突，见 isSizeChartApiUnsupportedMultiSizeAttr）
 * - mode=3 且单尺码/无次销售：传 1 条（有次销售时带该规格的 relate）
 * - 基础表（仅主销售）：只传 attribute_id + attribute_extra_value
 */
export function buildSizeAttributeList(form, sizeAttrs = []) {
  const chart = form.sizeChart || {};
  const list = [];
  const saleAttrId = resolveSaleAttributeId(form);
  // 纵轴以 SKU 规格为准；selectedSizeIds 仅作无 SKU 时的兜底
  const fromSkc = deriveSizeKeysFromSkcList(form.skcList);
  const selectedIds = (form.selectedSizeIds || []).map(String).filter(Boolean);
  const rowIds = fromSkc.length ? fromSkc : selectedIds;
  // 与 UI 一致：按关联签名去重
  const derivedIds = resolveSizeChartRows(rowIds, null, form.skcList).map((r) => String(r.id));
  const hasSaleAxis = Boolean(saleAttrId) && derivedIds.length > 0;

  const rowKeys = hasSaleAxis
    ? derivedIds
    : (saleAttrId ? [] : [SIZE_CHART_BASE_KEY]);

  const attrs = Array.isArray(sizeAttrs) ? sizeAttrs.filter((a) => a?.attributeId) : [];
  const attrById = new Map(attrs.map((a) => [String(a.attributeId), a]));
  const saleRowCount = hasSaleAxis ? rowKeys.length : 0;
  const { matrixAttrs, singleAttrs, unsupportedAttrs } = partitionSizeMeasureAttrs(attrs, saleRowCount);
  // 未传入模板属性时保持旧行为：图表里出现的属性都按矩阵处理
  const matrixIdSet = attrs.length
    ? new Set(matrixAttrs.map((a) => String(a.attributeId)))
    : null;
  const skipIdSet = new Set([
    ...singleAttrs.map((a) => String(a.attributeId)),
    ...unsupportedAttrs.map((a) => String(a.attributeId)),
  ]);

  // mode=3 且仅 1 个规格（或无次销售）：可传 1 组
  singleAttrs.forEach((attr) => {
    const attrId = String(attr.attributeId);
    const raw = pickSingleSelectMeasureValue(chart, attrId, rowKeys);
    if (!raw) return;
    let relate = null;
    if (hasSaleAxis) {
      relate = relateSaleFieldsForRowKey(rowKeys[0], saleAttrId);
      if (!relate) return;
    }
    pushSizeMeasureItem(list, attr, raw, relate);
  });

  // 同一 attribute_id + 同一规格关联只保留一条
  const seenCell = new Set();

  rowKeys.forEach((key) => {
    let measures = chart[key];
    // 兼容：SKU key 为 custom:xxx，图表残留明文名
    if ((!measures || typeof measures !== 'object') && isCustomSpecKey(key)) {
      const label = customLabelFromKey(key);
      if (chart[label] && typeof chart[label] === 'object') measures = chart[label];
    }
    if (!measures || typeof measures !== 'object') return;

    const relate = hasSaleAxis ? relateSaleFieldsForRowKey(key, saleAttrId) : null;
    if (hasSaleAxis && !relate) return;

    const relateSig = relate
      ? (relate.relate_sale_attribute_value_id != null
        ? `id:${relate.relate_sale_attribute_value_id}`
        : `v:${relate.relate_sale_attribute_value || ''}`)
      : 'base';

    Object.entries(measures).forEach(([attrId, extra]) => {
      const idKey = String(attrId);
      if (skipIdSet.has(idKey)) return;
      // 店铺类目尺码属性就绪后，丢掉官方模板/全局类目残留的未知尺寸列
      if (attrById.size > 0 && !attrById.has(idKey)) return;
      if (matrixIdSet && !matrixIdSet.has(idKey)) {
        if (attrById.has(idKey)) return;
      }
      if (extra === '' || extra == null) return;
      const raw = String(extra).trim();
      if (!raw) return;
      const cellKey = `${idKey}|${relateSig}`;
      if (seenCell.has(cellKey)) return;
      seenCell.add(cellKey);
      list.push({
        attribute_id: Number(attrId),
        attribute_extra_value: raw,
        ...(relate || {}),
      });
    });
  });

  if (!list.length && !(sizeAttrs || []).length) return undefined;
  return list.length ? list : undefined;
}

/** 提交前收敛 sizeChart：丢掉非 SKU 规格行；剥离多尺码下 API 不支持的 mode=3 列 */
export function pruneSizeChartForSubmit(form = {}, sizeAttrs = []) {
  const chart = form.sizeChart && typeof form.sizeChart === 'object' ? { ...form.sizeChart } : {};
  const fromSkc = deriveSizeKeysFromSkcList(form.skcList);
  const selectedIds = (form.selectedSizeIds || []).map(String).filter(Boolean);
  const keepKeys = [...(fromSkc.length ? fromSkc : selectedIds)];
  const keepSet = new Set(keepKeys);
  const saleRowCount = keepKeys.length;
  const { matrixAttrs, singleAttrs, unsupportedAttrs } = partitionSizeMeasureAttrs(sizeAttrs, saleRowCount);
  const matrixIds = matrixAttrs.map((a) => String(a.attributeId));
  const singleIds = singleAttrs.map((a) => String(a.attributeId));
  const dropIds = new Set(unsupportedAttrs.map((a) => String(a.attributeId)));
  const knownMeasureIds = new Set([...matrixIds, ...singleIds]);

  const next = {};
  if (singleIds.length) {
    const base = { ...(chart[SIZE_CHART_BASE_KEY] || {}) };
    singleIds.forEach((attrId) => {
      const v = pickSingleSelectMeasureValue(chart, attrId, keepKeys);
      if (v) base[attrId] = v;
      else delete base[attrId];
    });
    matrixIds.forEach((attrId) => { delete base[attrId]; });
    dropIds.forEach((attrId) => { delete base[attrId]; });
    if (knownMeasureIds.size > 0) {
      Object.keys(base).forEach((attrId) => {
        if (!knownMeasureIds.has(String(attrId))) delete base[attrId];
      });
    }
    if (Object.keys(base).length) next[SIZE_CHART_BASE_KEY] = base;
  }

  keepSet.forEach((key) => {
    const src = chart[key];
    if (!src || typeof src !== 'object') return;
    const cell = {};
    Object.entries(src).forEach(([attrId, extra]) => {
      if (singleIds.includes(String(attrId)) || dropIds.has(String(attrId))) return;
      if (knownMeasureIds.size > 0 && !knownMeasureIds.has(String(attrId))) return;
      if (extra === '' || extra == null) return;
      cell[attrId] = String(extra).trim();
    });
    if (Object.keys(cell).length) next[key] = cell;
  });

  return next;
}

const MULTI_VALUE_SEP = /[,，;；|｜]/;

/** 从规则/名称中解析数值区间，如 [0.1~99.9]、0.1-99.9 */
export function extractNumericRange(...texts) {
  for (const raw of texts) {
    const text = String(raw || '');
    if (!text) continue;
    const bracket = text.match(/\[\s*(\d+(?:\.\d+)?)\s*[~～\-–—]\s*(\d+(?:\.\d+)?)\s*\]/);
    if (bracket) {
      const min = Number(bracket[1]);
      const max = Number(bracket[2]);
      if (Number.isFinite(min) && Number.isFinite(max)) return { min, max };
    }
    const plain = text.match(/^(\d+(?:\.\d+)?)\s*[~～\-–—]\s*(\d+(?:\.\d+)?)$/);
    if (plain) {
      const min = Number(plain[1]);
      const max = Number(plain[2]);
      if (Number.isFinite(min) && Number.isFinite(max) && min < max) return { min, max };
    }
  }
  return null;
}

function attrRules(attr) {
  return Array.isArray(attr?.rules) ? attr.rules : [];
}

function allowedConnectors(attr) {
  const type5 = attrRules(attr).find((r) => Number(r.conditionType) === 5);
  if (!type5) return [];
  return String(type5.value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function maxDecimalPlaces(attr) {
  const type3 = attrRules(attr).find((r) => Number(r.conditionType) === 3);
  if (type3) {
    const n = Number(type3.value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  if (attrRules(attr).some((r) => Number(r.conditionType) === 5)) return 2;
  return null;
}

function requiresPositiveInteger(attr) {
  const rules = attrRules(attr);
  if (rules.some((r) => Number(r.conditionType) === 1)) return true;
  if (rules.some((r) => [3, 5].includes(Number(r.conditionType)))) return false;
  if (rules.some((r) => Number(r.conditionType) === 4)) return false; // 自然数含 0，另分支
  // 尺码表 extra_value 为 string，平台卖家后台为文本框，支持正数最多 2 位小数。
  // mode=0「仅正整数」是普通商品属性手输说明，不套用到 size_attribute_list。
  return false;
}

function requiresNaturalNumber(attr) {
  return attrRules(attr).some((r) => Number(r.conditionType) === 4)
    && !attrRules(attr).some((r) => [1, 3, 5].includes(Number(r.conditionType)));
}

function digitLengthOk(numText, attr) {
  const rules = attrRules(attr).filter((r) => [1, 4].includes(Number(r.conditionType)));
  for (const rule of rules) {
    const op = Number(rule.conditionOperator || 0);
    const limit = Number(rule.value);
    if (!op || !Number.isFinite(limit)) continue;
    const digits = String(numText).replace(/^0+(?=\d)/, '').length || 1;
    if (op === 1 && !(digits > limit)) return false;
    if (op === 2 && !(digits < limit)) return false;
  }
  return true;
}

function splitByAllowedConnectors(text, connectors) {
  if (!connectors.length) return [text];
  const escaped = connectors
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((c) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`(?:${escaped.join('|')})`);
  return text.split(re).map((s) => s.trim()).filter((s) => s !== '');
}

function validateNumberPart(part, attr, { allowZero = false, maxDecimals = 2 } = {}) {
  if (!part || !/^\d+(\.\d+)?$/.test(part)) {
    return { ok: false, message: '须为有效数字，不能填写文字或其它符号' };
  }
  // 禁止 00 / 02 / 01 等前导零；小数仅允许 0.x 形式的前导 0
  if (part.includes('.')) {
    const [intPart, decimals = ''] = part.split('.');
    if (maxDecimals <= 0) {
      return { ok: false, message: '须为正整数' };
    }
    if (!decimals.length || decimals.length > maxDecimals) {
      return { ok: false, message: `小数最多 ${maxDecimals} 位` };
    }
    if (!/^\d+$/.test(decimals)) {
      return { ok: false, message: '须为有效数字' };
    }
    if (intPart !== '0' && /^0\d+/.test(intPart)) {
      return { ok: false, message: '整数部分不能有前导零' };
    }
    if (intPart.length > 1 && intPart.startsWith('0')) {
      return { ok: false, message: '整数部分不能有前导零' };
    }
  } else if (/^0\d+/.test(part) || part === '00') {
    return { ok: false, message: '不能有前导零（如 02、00）' };
  }

  const num = Number(part);
  if (!Number.isFinite(num)) return { ok: false, message: '须为有效数字' };
  if (allowZero) {
    if (num < 0 || !Number.isInteger(num)) {
      return { ok: false, message: '须为自然数' };
    }
  } else if (!(num > 0)) {
    return { ok: false, message: '须为大于 0 的数字' };
  }
  if (!digitLengthOk(part.split('.')[0], attr)) {
    return { ok: false, message: '数字位数不符合平台规则' };
  }
  return { ok: true, num };
}

/**
 * 按 attribute_mode + rule_info_list 校验单个尺码格。
 * @returns {{ ok: boolean, message?: string }}
 */
export function checkSizeMeasureValue(value, attr = null) {
  const text = String(value ?? '').trim();
  if (!text) return { ok: false, message: '不能为空' };

  // 纯文字 / 混入字母
  if (/[a-zA-Z\u4e00-\u9fff]/.test(text)) {
    return { ok: false, message: '须为有效数字，不能填写文字' };
  }

  // 单选/手输：禁止逗号等「多个值」写法
  if (MULTI_VALUE_SEP.test(text) || /\d\s+\d/.test(text)) {
    return { ok: false, message: '录入方式为单选，不允许录入多个值' };
  }

  const connectors = allowedConnectors(attr);
  const parts = splitByAllowedConnectors(text, connectors);
  if (!parts.length) {
    return { ok: false, message: '须为有效数字，不能只填连接符' };
  }
  if (parts.length > 1 && !connectors.length) {
    return { ok: false, message: '录入方式为单选，不允许录入多个值' };
  }
  if (parts.length > 2) {
    return { ok: false, message: '区间仅支持两个数值' };
  }
  // 使用了未允许的连接符（含单独的 - / ~）
  if (/[~～\-/–—]/.test(text) && !connectors.some((c) => text.includes(c))) {
    return { ok: false, message: '当前属性不支持区间填写' };
  }

  const intOnly = requiresPositiveInteger(attr);
  const natural = requiresNaturalNumber(attr);
  const decimals = intOnly || natural ? 0 : (maxDecimalPlaces(attr) ?? 2);

  const nums = [];
  for (const part of parts) {
    const checked = validateNumberPart(part, attr, {
      allowZero: natural && parts.length === 1,
      maxDecimals: decimals,
    });
    if (!checked.ok) {
      if (intOnly && /须为有效数字|前导零|大于 0|正整数/.test(checked.message || '')) {
        return {
          ok: false,
          message: checked.message.includes('前导零')
            ? checked.message
            : (checked.message.includes('大于 0') ? checked.message : '须为正整数'),
        };
      }
      return checked;
    }
    nums.push(checked.num);
  }

  const range = extractNumericRange(
    ...(attrRules(attr).map((r) => r.value)),
    attr?.name,
    attr?.nameEn,
    attr?.remark,
  );
  if (range) {
    for (const n of nums) {
      if (n < range.min || n > range.max) {
        return {
          ok: false,
          message: `数值范围须在[${range.min}~${range.max}]`,
        };
      }
    }
  }

  return { ok: true };
}

/**
 * 兼容旧调用：无 attr 时按「正数最多 2 位小数」。
 * 有 attr 时走平台 mode/rule。
 */
export function isValidSizeMeasureValue(value, attr = null) {
  if (!attr) {
    const text = String(value ?? '').trim();
    if (!text) return false;
    if (!/^\d+(\.\d{1,2})?$/.test(text)) return false;
    return Number(text) > 0;
  }
  return checkSizeMeasureValue(value, attr).ok;
}

/** 输入过滤：默认允许数字与小数点；仅正整数/自然数规则时去掉小数点 */
export function sanitizeSizeMeasureInput(raw, attr = null) {
  const text = String(raw ?? '');
  const connectors = new Set(allowedConnectors(attr));
  const intOnly = requiresPositiveInteger(attr)
    || (requiresNaturalNumber(attr) && maxDecimalPlaces(attr) == null);
  return [...text].filter((ch) => {
    if (ch >= '0' && ch <= '9') return true;
    if (!intOnly && ch === '.') return true;
    if (connectors.has(ch)) return true;
    return false;
  }).join('');
}

/** 与 SizeChartPanel 一致的行集合 */
export function resolveSizeChartFormRows({
  sizeAttr = null,
  selectedSizeIds = [],
  skcList = [],
  measureAttrs = [],
} = {}) {
  const hasSecondary = Boolean(sizeAttr?.attributeId);
  const needsAxis = sizeChartNeedsSaleAxis(sizeAttr, selectedSizeIds, skcList);
  if (hasSecondary) {
    return needsAxis ? resolveSizeChartRows(selectedSizeIds, sizeAttr, skcList) : [];
  }
  return (measureAttrs || []).length
    ? [{ id: SIZE_CHART_BASE_KEY, name: '尺寸', custom: false }]
    : [];
}

/**
 * 尺码表完整校验（模板保存 / 发品共用）。
 * @returns {{ ok: true } | { ok: false, message: string, section?: string, field?: string }}
 */
export function validateSizeChartForm({
  sizeChart = {},
  measureAttrs = [],
  sizeAttr = null,
  selectedSizeIds = [],
  skcList = [],
} = {}) {
  const attrs = Array.isArray(measureAttrs) ? measureAttrs : [];
  if (!attrs.length) return { ok: true };

  const chartRowsPreview = resolveSizeChartFormRows({
    sizeAttr,
    selectedSizeIds,
    skcList,
    measureAttrs: attrs,
  });
  const saleRowCount = sizeAttr?.attributeId ? chartRowsPreview.length : 0;
  const { matrixAttrs, singleAttrs, unsupportedAttrs } = partitionSizeMeasureAttrs(attrs, saleRowCount);

  const requiredUnsupported = unsupportedAttrs.filter((a) => a.required);
  if (requiredUnsupported.length) {
    const names = requiredUnsupported
      .map((a) => attributeDisplayLabel(a) || a.name || a.attributeId)
      .join('、');
    return {
      ok: false,
      message: `尺寸属性「${names}」为平台单选，API 在多尺码下无法填写；请只保留一个尺码规格，或更换类目`,
      section: 'size',
      field: 'sizeChart',
    };
  }

  const chartRows = resolveSizeChartFormRows({
    sizeAttr,
    selectedSizeIds,
    skcList,
    measureAttrs: matrixAttrs.length ? matrixAttrs : attrs.filter((a) => !unsupportedAttrs.includes(a)),
  });

  if (!chartRows.length && matrixAttrs.some((a) => a.required)) {
    return {
      ok: false,
      message: `请先选择${attributeDisplayLabel(sizeAttr) || '其他规格'}后再填写必填尺码表`,
      section: 'size',
      field: 'sizeChart',
    };
  }

  let anyFilled = false;
  const rowKeys = chartRows.map((r) => String(r.id));

  // mode=3（仅单尺码场景会进入 singleAttrs）：整表只校验一个值
  for (const attr of singleAttrs) {
    const label = attributeDisplayLabel(attr) || attr.name || attr.attributeId;
    const text = pickSingleSelectMeasureValue(sizeChart, attr.attributeId, rowKeys);
    if (!text) {
      if (attr.required) {
        return {
          ok: false,
          message: `尺寸表填写数值不能为空：${label}（单选，全商品仅填一次）`,
          section: 'size',
          field: 'sizeChart',
        };
      }
      continue;
    }
    anyFilled = true;
    const checked = checkSizeMeasureValue(text, attr);
    if (!checked.ok) {
      return {
        ok: false,
        message: `${label}：${checked.message}`,
        section: 'size',
        field: 'sizeChart',
      };
    }
  }

  for (const row of chartRows) {
    const measures = sizeChart?.[row.id] || {};
    for (const attr of matrixAttrs) {
      const label = attributeDisplayLabel(attr) || attr.name || attr.attributeId;
      const text = String(measures[attr.attributeId] ?? '').trim();
      const rowHint = row.name && row.name !== '尺寸' ? `（${row.name}）` : '';
      if (!text) {
        if (attr.required) {
          return {
            ok: false,
            message: `尺寸表填写数值不能为空：${label}${rowHint}`,
            section: 'size',
            field: 'sizeChart',
          };
        }
        continue;
      }
      anyFilled = true;
      const checked = checkSizeMeasureValue(text, attr);
      if (!checked.ok) {
        return {
          ok: false,
          message: `${label}${rowHint}：${checked.message}`,
          section: 'size',
          field: 'sizeChart',
        };
      }
    }
  }

  const fillableAttrs = [...matrixAttrs, ...singleAttrs];
  if (!anyFilled) {
    if (!fillableAttrs.length) return { ok: true };
    return {
      ok: false,
      message: fillableAttrs.some((a) => a.required) ? '请填写尺寸表必填项' : '请至少填写一项尺寸数值',
      section: 'size',
      field: 'sizeChart',
    };
  }

  // 与提交体对齐：有次销售时必须能组出带 relate_sale_* 的矩阵项（若存在矩阵属性）
  const saleAttrId = resolveSaleAttributeId({
    sizeSaleAttributeId: sizeAttr?.attributeId,
    skcList,
    selectedSizeIds,
    sizeChart,
  });
  if ((sizeAttr?.attributeId || saleAttrId) && matrixAttrs.length && chartRows.length) {
    const built = buildSizeAttributeList({
      sizeChart,
      selectedSizeIds: selectedSizeIds?.length ? selectedSizeIds : chartRows.map((r) => r.id),
      sizeSaleAttributeId: sizeAttr?.attributeId || saleAttrId || '',
      skcList,
    }, attrs);
    const matrixBuilt = (built || []).filter((row) => {
      const mode = Number(attrByIdMode(attrs, row.attribute_id));
      return mode !== 3;
    });
    if (!matrixBuilt.length && matrixAttrs.some((a) => {
      return chartRows.some((r) => String(sizeChart?.[r.id]?.[a.attributeId] ?? '').trim());
    })) {
      return {
        ok: false,
        message: '尺码表无法关联规格值，请重新勾选尺寸规格后再填写',
        section: 'size',
        field: 'sizeChart',
      };
    }
    const requiredMatrix = matrixAttrs.filter((a) => a.required);
    const rowCount = chartRows.length;
    for (const attr of requiredMatrix) {
      const attrId = String(attr.attributeId);
      const n = (built || []).filter((row) => String(row.attribute_id) === attrId).length;
      if (n < rowCount) {
        const label = attributeDisplayLabel(attr) || attrId;
        return {
          ok: false,
          message: `尺寸表「${label}」未完整关联到每个规格`,
          section: 'size',
          field: 'sizeChart',
        };
      }
    }
  }

  return { ok: true };
}

function attrByIdMode(attrs, attributeId) {
  const hit = (attrs || []).find((a) => String(a.attributeId) === String(attributeId));
  return hit?.mode;
}

/**
 * 将模板尺码表套用到当前表单行。
 * 优先按行 id 匹配，其次按行名称（忽略大小写）匹配。
 */
export function applySizeChartTemplatePayload(payload, chartRows = [], measureAttrs = []) {
  const sourceChart = payload?.sizeChart && typeof payload.sizeChart === 'object'
    ? payload.sizeChart
    : {};
  const sourceRows = Array.isArray(payload?.sizeRows) ? payload.sizeRows : [];
  const nameToSourceId = new Map();
  sourceRows.forEach((row) => {
    const name = String(row?.name || '').trim().toLowerCase();
    if (name && row?.id != null) nameToSourceId.set(name, String(row.id));
  });
  Object.keys(sourceChart).forEach((id) => {
    // 若 sizeRows 缺失，仍可用 id 自身
    if (!nameToSourceId.has(String(id).toLowerCase())) {
      nameToSourceId.set(String(id).toLowerCase(), String(id));
    }
  });

  const { matrixAttrs, singleAttrs } = partitionSizeMeasureAttrs(measureAttrs);
  const next = {};
  let filled = 0;
  chartRows.forEach((row) => {
    const byId = sourceChart[row.id];
    const byNameId = nameToSourceId.get(String(row.name || '').trim().toLowerCase());
    const sourceMeasures = (byId && typeof byId === 'object')
      ? byId
      : (byNameId && typeof sourceChart[byNameId] === 'object' ? sourceChart[byNameId] : null);
    if (!sourceMeasures) return;
    const cell = {};
    matrixAttrs.forEach((attr) => {
      const attrId = String(attr.attributeId);
      const value = sourceMeasures[attrId] ?? sourceMeasures[attr.attributeId];
      if (value === '' || value == null) return;
      cell[attrId] = String(value);
      filled += 1;
    });
    if (Object.keys(cell).length) next[row.id] = cell;
  });

  if (singleAttrs.length) {
    const base = {};
    const rowKeys = [
      ...Object.keys(sourceChart),
      ...chartRows.map((r) => String(r.id)),
    ];
    singleAttrs.forEach((attr) => {
      const attrId = String(attr.attributeId);
      const value = pickSingleSelectMeasureValue(sourceChart, attrId, rowKeys);
      if (!value) return;
      base[attrId] = value;
      filled += 1;
    });
    if (Object.keys(base).length) next[SIZE_CHART_BASE_KEY] = base;
  }

  return { sizeChart: next, filled };
}

/** 从当前编辑态组装可保存的模板 payload */
export function buildSizeChartTemplatePayload({
  sizeChart = {},
  chartRows = [],
  measureAttrs = [],
  sizeSaleAttributeId = '',
  productTypeId = '',
  categoryId = '',
  categoryPathLabel = '',
} = {}) {
  return {
    sizeChart,
    sizeRows: (chartRows || []).map((row) => ({
      id: String(row.id),
      name: row.name || String(row.id),
      custom: Boolean(row.custom),
    })),
    measureAttrs: (measureAttrs || []).map((attr) => ({
      attributeId: String(attr.attributeId),
      name: attributeDisplayLabelSafe(attr),
      required: Boolean(attr.required),
    })),
    measureAttrIds: (measureAttrs || []).map((attr) => String(attr.attributeId)),
    sizeSaleAttributeId: sizeSaleAttributeId ? String(sizeSaleAttributeId) : '',
    productTypeId: productTypeId ? String(productTypeId) : '',
    categoryId: categoryId ? String(categoryId) : '',
    categoryPathLabel: categoryPathLabel ? String(categoryPathLabel) : '',
  };
}

function attributeDisplayLabelSafe(attr) {
  if (!attr) return '';
  return attributeDisplayLabel(attr) || String(attr.name || attr.nameEn || attr.attributeId || '').trim();
}

